<template>
  <div class="dashboard-shell">
    <AppTopbar />
    <main class="page-shell">
      <div class="container-readable stack-lg">
        
        <!-- Hero Banner עליון בעיצוב המותג -->
        <section class="hero-banner">
          <div class="hero-container">
            <div class="hero-content">
              <router-link to="/" class="back-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                חזרה לאזור האישי
              </router-link>

              <h1 class="hero-title">נתוני שימוש</h1>
              <p class="hero-subtitle">תצוגה ברורה ומפורטת של כל השירותים והניצול במחזור הנוכחי.</p>
            </div>

            <button class="refresh-btn" :disabled="loadingUsage" @click="loadUsage" title="רענון">
              <svg class="refresh-icon" :class="{ spinning: loadingUsage }" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <path d="M23 4v6h-6M1 20v-6h6"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              <span>{{ loadingUsage ? 'מרענן...' : 'רענון נתונים' }}</span>
            </button>
          </div>
        </section>

        <!-- פאנל נתוני השימוש -->
        <section class="surface card-block usage-panel stack-md">
          <div v-if="loadingUsage" class="loading-state muted">
            טוען נתוני שימוש...
          </div>

          <div v-else-if="usageError" class="stack-sm alert-inline">
            <p class="alert-text">{{ usageError }}</p>
            <button class="btn btn-ghost" @click="loadUsage">לנסות שוב</button>
          </div>

          <template v-else-if="usageData">
            <!-- מדדים מרכזיים בגריד קוביות -->
            <div class="usage-metrics-grid">
              <article class="metric-card" v-if="usageData.incoming_calls != null">
                <div class="metric-icon-wrap phone">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <div class="metric-data">
                  <p class="metric-label">שיחות נכנסות</p>
                  <p class="metric-value">{{ usageData.incoming_calls }}</p>
                  <p class="muted sub-text">סה״כ במחזור הנוכחי</p>
                </div>
              </article>

              <article class="metric-card" v-if="usageData.outgoing_calls != null">
                <div class="metric-icon-wrap phone-out">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    <line x1="15" y1="9" x2="21" y2="3"/>
                    <polyline points="16 3 21 3 21 8"/>
                  </svg>
                </div>
                <div class="metric-data">
                  <p class="metric-label">שיחות יוצאות</p>
                  <p class="metric-value">{{ usageData.outgoing_calls }}</p>
                  <p class="muted sub-text">סה״כ במחזור הנוכחי</p>
                </div>
              </article>

              <article class="metric-card" v-if="usageData.sms_used != null">
                <div class="metric-icon-wrap sms">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </div>
                <div class="metric-data">
                  <p class="metric-label">הודעות SMS</p>
                  <p class="metric-value">{{ usageData.sms_used }} <span class="unit">הודעות</span></p>
                  <p class="muted sub-text" v-if="usageData.sms_total">מתוך {{ usageData.sms_total }} הודעות</p>
                </div>
              </article>

              <article class="metric-card" v-if="usageData.data_used != null">
                <div class="metric-icon-wrap data">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M5 12.55a11 11 0 0 1 14.08 0"/>
                    <path d="M1.42 9a16 16 0 0 1 21.16 0"/>
                    <path d="M8.53 16.11a6 6 0 0 1 6.95 0"/>
                    <line x1="12" y1="20" x2="12.01" y2="20"/>
                  </svg>
                </div>
                <div class="metric-data">
                  <p class="metric-label">נתוני אינטרנט</p>
                  <p class="metric-value">{{ formatData(usageData.data_used) }}</p>
                  <p class="muted sub-text" v-if="usageData.data_total">מתוך {{ formatData(usageData.data_total) }}</p>
                </div>
              </article>
            </div>

            <!-- סרגלי ניצול עבור שירותים עם מכסה -->
            <div v-if="servicesWithQuota.length" class="quota-section stack-sm">
              <h2 class="section-title">חבילות ומכסות פעילות</h2>
              <div class="usage-track-list">
                <article
                  v-for="svc in servicesWithQuota"
                  :key="svc.name"
                  class="usage-track-card stack-sm"
                >
                  <div class="inline-row space-between usage-track-head">
                    <h3 class="usage-track-title">{{ translateService(svc.name) }}</h3>
                    <span class="usage-track-value">{{ formatValue(svc.usage, svc.unit, svc.name) }} / {{ formatValue(svc.total, svc.unit, svc.name) }}</span>
                  </div>
                  
                  <div class="usage-bar">
                    <div
                      class="usage-bar-fill"
                      :class="usageClass(pct(svc.usage, svc.total))"
                      :style="{ width: pct(svc.usage, svc.total) + '%' }"
                    ></div>
                  </div>
                  
                  <div class="usage-track-meta">
                    <span class="pct-badge" :class="usageClass(pct(svc.usage, svc.total))">
                      {{ pct(svc.usage, svc.total) }}% בשימוש
                    </span>
                  </div>
                </article>
              </div>
            </div>

            <!-- שירותים נוספים וחבילות בצורת קוביות -->
            <div v-if="servicesNoQuota.length" class="extra-services stack-sm">
              <h2 class="section-title">שירותים נוספים / חבילות</h2>
              <div class="extra-services-grid">
                <div v-for="svc in servicesNoQuota" :key="svc.name" class="extra-service-card">
                  <div class="extra-icon-wrap">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  </div>
                  <div class="extra-service-info">
                    <span class="extra-service-name">{{ translateService(svc.name) }}</span>
                    <span class="extra-service-usage">{{ formatValue(svc.usage, svc.unit, svc.name) }} בשימוש</span>
                  </div>
                </div>
              </div>
            </div>
          </template>

          <p v-else class="empty-state muted">אין נתוני שימוש זמינים עבור מנוי זה.</p>
        </section>

      </div>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import AppFooter from '@/components/AppFooter.vue'
import AppTopbar from '@/components/AppTopbar.vue'

const SERVICE_LABELS = {
  'Outgoing Calls': 'שיחות יוצאות (דקות)',
  'Incoming Calls': 'שיחות נכנסות (דקות)',
  'SMS': 'הודעות SMS',
  'Data': 'גלישה (נתונים)',
  'Internet': 'גלישה (נתונים)',
}

export default {
  name: 'UsageDetails',
  components: { AppTopbar, AppFooter },
  data() {
    return {
      loadingUsage: false,
      usageData: null,
      usageError: '',
    }
  },
  computed: {
    servicesWithQuota() {
      return (this.usageData?.services || []).filter(s => s.total > 0)
    },
    servicesNoQuota() {
      return (this.usageData?.services || []).filter(s => !(s.total > 0))
    },
  },
  mounted() {
    if (this.isNull(localStorage.getItem('user_token'))) { this.$router.push('/login'); return }
    this.loadUsage()
  },
  methods: {
    loadUsage() {
      this.loadingUsage = true; this.usageError = ''; this.usageData = null
      this.api({ action: 'get_my_usage', method: 'get' }, (response) => {
        if (response?.data?.success && response.data.usage) {
          this.usageData = response.data.usage
        } else {
          this.usageError = this.usageErrorText(response?.data?.error)
        }
        this.loadingUsage = false
      }, () => {
        this.usageError = 'אירעה שגיאה זמנית בטעינת הנתונים.'
        this.loadingUsage = false
      })
    },
    usageErrorText(code) {
      const map = {
        not_logged_in: 'החיבור פג. יש להתחבר מחדש.',
        no_domain_user: 'החשבון אינו מקושר לקו פעיל. יש לפנות לשירות לקוחות.',
        no_endpoints: 'לא נמצא קו פעיל עבור החשבון. יש לפנות לשירות לקוחות.',
        no_endpoint_id: 'לא נמצא קו פעיל עבור החשבון. יש לפנות לשירות לקוחות.',
        no_usage_data: 'אין נתוני שימוש זמינים כרגע. נסה שוב מאוחר יותר.',
      }
      return map[code] || 'לא ניתן לטעון את נתוני השימוש כרגע. נסה שוב מאוחר יותר.'
    },
    pct(used, total) {
      if (!total || total <= 0) return 0
      return Math.min(100, Math.round((used / total) * 100))
    },
    usageClass(p) {
      if (p >= 90) return 'bar-danger'
      if (p >= 70) return 'bar-warning'
      return 'bar-ok'
    },
    translateService(name) {
      for (const [key, label] of Object.entries(SERVICE_LABELS)) {
        if (name.toLowerCase().includes(key.toLowerCase())) return label
      }
      return name
    },
    formatValue(val, unit, name) {
      if (val == null) return '—'
      const u = (unit || '').toLowerCase()
      const n = (name || '').toLowerCase()
      if (u === 'mb' || u === 'gb' || u.includes('data') || n.includes('data') || n.includes('internet')) return this.formatData(val)
      if (u === 'min' || u === 'minute' || n.includes('call')) return this.roundNum(val) + ' דק׳'
      if (u === 'sms' || u.includes('sms') || n.includes('sms')) return this.roundNum(val) + ' הודעות'
      return String(this.roundNum(val))
    },
    formatData(val) {
      if (val == null) return '—'
      if (val >= 1024) return (val / 1024).toFixed(1) + ' GB'
      return this.roundNum(val) + ' MB'
    },
    roundNum(val) {
      const n = Number(val)
      if (!isFinite(n)) return 0
      return Math.round(n * 10) / 10
    },
  }
}
</script>

<style lang="scss" scoped src="@/styles/pages/usage-details.scss"></style>